// ─── Quotation Conflict View ─────────────────────────────────────────────────
//
// G2.18: Production-grade conflict renderer for QUOTATION sync conflicts.
// Teal accent to match ENTITY_TYPE_CONFIG.QUOTATION.
//
// Traceability: FR-SYNC-013, US-BILL-019.

import { EmptyState } from '../../CompositeComponents/EmptyState';
import { GLASS_FORM, cn } from '../../../constants/design-tokens';
import { getQuotationSchema, formatValue, formatPaiseDecimal as formatPaise, computeQuotationLineTotal } from '@bbpos/utils';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import type { TaxRegimePresentation } from '../../../utils/tax-regime';
import type { CurrencyCode } from '@bbpos/types';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';
import { ServerItemsEmptyState } from './ServerItemsEmptyState';

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuotationItem {
  product_id?: string;
  name?: string;
  sku?: string;
  unit?: string;
  quantity?: number;
  unit_price?: number;
  discount?: number;
  gst_rate?: number;
  [extra: string]: unknown;
}

export interface QuotationConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
  /**
   * Resolved tax regime (from `@bbpos/ui`'s `resolveTaxRegime` /
   * `useTaxRegime()`). India ⇒ `Tax (GST)` schema row + `GST` column header
   * (byte-identical); GCC ⇒ `Tax (VAT)` / `VAT`. Defaults to the India
   * (`INR`) regime. `@bbpos/ui` stays hook-free — consumers pass this in from
   * `useTaxRegime()`.
   */
  taxRegime?: TaxRegimePresentation;
  /**
   * Tenant currency for money fields/columns. Defaults to `'INR'` so the
   * schema and line-item amounts are byte-identical to the pre-multi-currency
   * behaviour; non-INR threads the correct symbol/subunit/decimals. `@bbpos/ui`
   * stays hook-free — consumers pass this in from `useTenantCurrency()`.
   */
  currency?: CurrencyCode;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function coerceItems(value: unknown): QuotationItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter((it): it is QuotationItem => !!it && typeof it === 'object');
}

// ─── Items table ─────────────────────────────────────────────────────────────

function ItemsTable({
  items,
  side,
  taxName,
  currency,
}: {
  items: QuotationItem[];
  side: 'client' | 'server';
  taxName: string;
  currency: CurrencyCode;
}) {
  if (items.length === 0) {
    return (
      <EmptyState
        variant="table"
        title={side === 'client' ? 'No items in client payload' : 'No items in server record'}
      />
    );
  }

  return (
    <div className={cn(GLASS_FORM, 'overflow-x-auto')}>
      <table className="w-full text-xs">
        <caption className="sr-only">
          Quotation line items from the {side === 'client' ? 'offline device' : 'server record'}
        </caption>
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
            <th className="text-left px-3 py-2 font-medium">Product</th>
            <th className="text-right px-3 py-2 font-medium">Qty</th>
            <th className="text-right px-3 py-2 font-medium">Unit Price</th>
            <th className="text-right px-3 py-2 font-medium">Discount</th>
            <th className="text-right px-3 py-2 font-medium">{taxName}</th>
            <th className="text-right px-3 py-2 font-medium">Line Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const lineTotal = computeQuotationLineTotal(item);
            return (
              <tr
                key={item.product_id ?? `row-${idx}`}
                className={cn(
                  'transition-colors',
                  idx % 2 === 1 && 'bg-neutral-50/60 dark:bg-neutral-800/40',
                  'animate-slide-up',
                )}
                style={{ animationDelay: `${Math.round(Math.log2(idx + 1) * 100)}ms` }}
              >
                <td className="px-3 py-2 text-neutral-800 dark:text-neutral-200">
                  <div className="flex flex-col">
                    <span className="font-medium">{item.name ?? '\u2014'}</span>
                    {item.sku && (
                      <span className="text-[10px] font-mono text-neutral-500 dark:text-neutral-400">
                        {item.sku}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {Number(item.quantity ?? 0)}
                  {item.unit && (
                    <span className="ml-1 text-[10px] uppercase text-neutral-400">{item.unit}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {formatPaise(item.unit_price, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                  {formatPaise(item.discount, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-600 dark:text-neutral-400">
                  {item.gst_rate != null ? `${item.gst_rate}%` : '\u2014'}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                  {formatPaise(lineTotal, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

export function QuotationConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
  taxRegime,
  currency = 'INR',
}: QuotationConflictViewProps) {
  // Hook-free: default to India (INR) so the schema and `GST` column header
  // are byte-identical to the pre-multi-currency behaviour.
  const regime = taxRegime ?? resolveTaxRegime('INR');
  const schema = getQuotationSchema(regime);
  const quoteNumber =
    (clientVersion.quote_number as string | undefined) ??
    (serverVersion.quote_number as string | undefined) ??
    'Unknown';

  const statusRaw =
    (clientVersion.status as string | undefined) ??
    (serverVersion.status as string | undefined);

  const items = coerceItems(clientVersion.items);
  const serverItems = coerceItems(serverVersion.items);
  const serverItemsLoaded = 'items' in serverVersion;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="quotation-conflict-view">
      <ConflictHeader
        entityLabel="Quotation Conflict"
        identifier={quoteNumber}
        accent={{
          from: 'from-teal-500/10',
          to: 'to-brand-500/10',
          ring: 'ring-teal-500/20',
          text: 'text-teal-700',
          darkText: 'dark:text-teal-300',
        }}
        tone="teal"
        badgeVariant="quote"
        badgeLabel={statusRaw ? formatValue('enum', statusRaw) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Quotation conflict header"
      />

      {/* Grouped field comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schema.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            currency={currency}
            delay={i * 60}
          />
        ))}
      </div>

      {/* Items section */}
      <section
        className="space-y-3"
        role="region"
        aria-label="Quotation line items comparison"
      >
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Line Items
          </h4>
          <span className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              Client (offline device)
            </div>
            <ItemsTable items={items} side="client" taxName={regime.taxName} currency={currency} />
          </div>
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400">
              Server
            </div>
            {serverItemsLoaded ? (
              <ItemsTable items={serverItems} side="server" taxName={regime.taxName} currency={currency} />
            ) : (
              <ServerItemsEmptyState label="line items" />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
